import React, { createContext, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { useApi } from 'shared/hooks'
import { COURIER_COMPANIES, getOrderMeta } from 'utils/api-routes.util'

import {
  IAllowedBusiness,
  IAllowedCountry,
  IAllowedCourier,
  IAllowedStatus,
  ILinkedStore,
  IMainMeta,
  IOrdersContextProps,
  IOrderSource,
  IPaymentGateway,
  ISalesPerson,
  IShippingMethod,
  IState,
  IStatusGroup,
  ITag,
  ITenantInfo,
  IUserInfo,
  IWarehouse,
} from '../../decl'

export const OrdersContext = createContext<IOrdersContextProps>({
  state: {
    clearFilterBtn: false,
    currentStatus: '',
    selectedStatus: '',
    ordersLength: 0,
    orderTags: [],
    allowedStatuses: [],
    orderStatuses: [],
    orderSources: [],
    couriers: [],
    linkedStores: [],
    allowed_countries: [],
    businessesList: [],
    salePersons: [],
    paymentGateways: [],
    shippingMethods: [],
    citiesList: [],
    groupedStatuses: [],
    warehouses: [],
    activeTab: 0,
    isEditing: false,
    is_editable: true,
    isDisabled: true,
    editPermissions: false,
    availableCountries: [],
    messages: [],
    userInfo: {
      id: 0,
      tenant_id: 0,
      name: '',
      user_email: '',
      permissions: [],
      is_otp_verified: false,
      token: '',
      active: true,
      created_at: {
        date: new Date().toISOString(),
        timezone_type: 3,
        timezone: 'UTC',
      },
      user_type: {
        id: 1,
        type: 'admin',
        description: 'Admin',
        count: null,
      },
    },
    tenantInfo: {
      business_name: '',
      default_business_name: '',
      default_country: '',
      default_currency: '',
      default_currency_symbol: '',
      default_timezone: '',
      ecom_store: '',
      store_id: 0,
      store_name: '',
      tenant_id: 0,
      website_url: '',
    },
  },
  orderMeta: {
    tags: [],
    warehouses: [],
    allowed_statuses: [],
    sales_persons: [],
    allowed_couriers: [],
    allowed_businesses: [],
    allowed_countries: [],
    order_sources: [],
    shipping_methods: [],
    linked_stores: [],
    payment_gateways: [],
  },
  setStateItem: () => {},
})

export const OrdersProvider: React.FC<{ children: React.ReactNode }> =
  React.memo(({ children }) => {
    const [orderMeta, setOrderMeta] = React.useState<IMainMeta>({
      tags: [],
      warehouses: [],
      allowed_statuses: [],
      sales_persons: [],
      allowed_couriers: [],
      allowed_businesses: [],
      allowed_countries: [],
      order_sources: [],
      shipping_methods: [],
      linked_stores: [],
      payment_gateways: [],
    })
    const [state, setState] = React.useState<IState>({
      clearFilterBtn: false,
      currentStatus: '',
      selectedStatus: '',
      ordersLength: 0,
      orderTags: [],
      allowedStatuses: [],
      orderStatuses: [],
      orderSources: [],
      couriers: [],
      linkedStores: [],
      allowed_countries: [],
      businessesList: [],
      salePersons: [],
      paymentGateways: [],
      shippingMethods: [],
      citiesList: [],
      groupedStatuses: [],
      warehouses: [],
      activeTab: 0,
      isEditing: false,
      is_editable: true,
      isDisabled: true,
      editPermissions: false,
      availableCountries: [],
      messages: [],
      userInfo: {
        id: 0,
        tenant_id: 0,
        name: '',
        user_email: '',
        permissions: [],
        is_otp_verified: false,
        token: '',
        active: true,
        created_at: {
          date: new Date().toISOString(),
          timezone_type: 3,
          timezone: 'UTC',
        },
        user_type: {
          id: 1,
          type: 'admin',
          description: 'Admin',
          count: null,
        },
      },
      tenantInfo: {
        business_name: '',
        default_business_name: '',
        default_country: '',
        default_currency: '',
        default_currency_symbol: '',
        default_timezone: '',
        ecom_store: '',
        store_id: 0,
        store_name: '',
        tenant_id: 0,
        website_url: '',
      },
    })

    const setStateItem = useCallback((key: string, value: any) => {
      setState((prev) => {
        return { ...prev, [key]: value }
      })
    }, [])

    const location = useLocation()

    useApi<{ data: IMainMeta }>(getOrderMeta, {
      onSuccess: (response) => {
        if (response?.data) {
          setOrderMeta(response.data)
          const meta = response.data
          if (meta) {
            const index = response.data?.allowed_couriers.findIndex(
              (company) =>
                company.company_id === COURIER_COMPANIES.one_logistics,
            )
            if (index !== -1) {
              const oneLogisticsCompany =
                response.data?.allowed_couriers.splice(index, 1)[0]
              response.data?.allowed_couriers.unshift(oneLogisticsCompany)
            }
            setStateItem('paymentGateways', meta['payment_gateways'] || [])
            setStateItem('salePersons', meta['sales_persons'] || [])
            setStateItem('businessesList', meta['allowed_businesses'] || [])
            setStateItem('availableCountries', meta['allowed_countries'] || [])
            setStateItem('couriers', meta['allowed_couriers'] || [])
            setStateItem('orderSources', meta['order_sources'] || [])
            setStateItem('allowedStatuses', meta['allowed_statuses'] || [])
            setStateItem('orderTags', meta['tags'] || [])
            setStateItem('shippingMethods', meta['shipping_methods'] || [])
            setStateItem('linkedStores', meta['linked_stores'] || [])
            setStateItem('warehouses', meta['warehouses'] || [])

            if (meta['allowed_countries'] && meta['allowed_countries'][0]) {
              setStateItem('citiesList', meta['allowed_countries'][0]['cities'])
            }
            setStateItem('warehouses', meta['warehouses'] || [])
          }
        }
      },
      skip:
        !localStorage.getItem('user_info') ||
        location?.pathname === '/auth/login' ||
        location?.pathname === '/onboarding',
    })

    React.useEffect(() => {
      if (
        location?.pathname === '/auth/login' ||
        location?.pathname === '/onboarding'
      ) {
        setOrderMeta({
          tags: [],
          warehouses: [],
          allowed_statuses: [],
          sales_persons: [],
          allowed_couriers: [],
          allowed_businesses: [],
          allowed_countries: [],
          order_sources: [],
          shipping_methods: [],
          linked_stores: [],
          payment_gateways: [],
        })
        setState({
          clearFilterBtn: false,
          currentStatus: '',
          selectedStatus: '',
          ordersLength: 0,
          orderTags: [],
          allowedStatuses: [],
          orderStatuses: [],
          orderSources: [],
          couriers: [],
          linkedStores: [],
          allowed_countries: [],
          businessesList: [],
          salePersons: [],
          paymentGateways: [],
          shippingMethods: [],
          citiesList: [],
          groupedStatuses: [],
          warehouses: [],
          activeTab: 0,
          isEditing: false,
          is_editable: true,
          isDisabled: true,
          editPermissions: false,
          availableCountries: [],
          messages: [],
          userInfo: {
            id: 0,
            tenant_id: 0,
            name: '',
            user_email: '',
            permissions: [],
            is_otp_verified: false,
            token: '',
            active: true,
            created_at: {
              date: new Date().toISOString(),
              timezone_type: 3,
              timezone: 'UTC',
            },
            user_type: {
              id: 1,
              type: 'admin',
              description: 'Admin',
              count: null,
            },
          },
          tenantInfo: {
            business_name: '',
            default_business_name: '',
            default_country: '',
            default_currency: '',
            default_currency_symbol: '',
            default_timezone: '',
            ecom_store: '',
            store_id: 0,
            store_name: '',
            tenant_id: 0,
            website_url: '',
          },
        })
      }
    }, [location])

    return (
      <OrdersContext.Provider
        value={{
          state,
          orderMeta,
          setStateItem,
        }}
      >
        {children}
      </OrdersContext.Provider>
    )
  })

OrdersProvider.displayName = 'OrdersProvider'

OrdersContext.displayName = 'OrdersContext'
